import { useCallback, useState, useEffect, useRef } from 'react';
import { useCurrentUser } from '@/hooks/use-current-user-org';
import { trpc } from '@/lib/trpc';
import type { RouterOutputs } from '@/lib/trpc';

export type WorkspaceWithChats = RouterOutputs['chat']['listWorkspaces']['workspaces'][number];

const DEFAULT_PAGE_SIZE = 20;

interface UseWorkspacesOptions {
  pageSize?: number;
}

function useWorkspacesInternal(options: UseWorkspacesOptions = {}) {
  const { pageSize = DEFAULT_PAGE_SIZE } = options;
  const { isAuthenticated, isLoading } = useCurrentUser();
  const [offset, setOffset] = useState(0);
  const [allWorkspaces, setAllWorkspaces] = useState<WorkspaceWithChats[]>([]);
  const [newlyLoadedIds, setNewlyLoadedIds] = useState<Set<string>>(new Set());
  const prevOffsetRef = useRef(0);

  const query = trpc.chat.listWorkspaces.useQuery(
    { offset, limit: pageSize },
    {
      enabled: isAuthenticated && !isLoading,
      staleTime: Infinity,
      refetchInterval: false,
      refetchOnWindowFocus: false,
    }
  );

  // Append new data when offset changes and data is loaded
  useEffect(() => {
    if (query.data?.workspaces) {
      if (offset === 0) {
        // Reset on initial load or refresh
        setAllWorkspaces(query.data.workspaces);
        setNewlyLoadedIds(new Set());
      } else if (offset > prevOffsetRef.current) {
        // Append when loading more, deduplicating by ID
        const newWorkspaces = query.data.workspaces;
        setAllWorkspaces((prev) => {
          const existingIds = new Set(prev.map((w) => w.id));
          const uniqueNew = newWorkspaces.filter((w) => !existingIds.has(w.id));
          return [...prev, ...uniqueNew];
        });
        // Track truly new IDs for highlight animation
        setNewlyLoadedIds((prev) => {
          const existingIds = new Set([...prev, ...allWorkspaces.map((w) => w.id)]);
          return new Set(newWorkspaces.filter((w) => !existingIds.has(w.id)).map((w) => w.id));
        });

        // Clear the highlight after animation completes
        setTimeout(() => {
          setNewlyLoadedIds(new Set());
        }, 1500);
      }
      prevOffsetRef.current = offset;
    }
  }, [query.data?.workspaces, offset]);

  const fetchNextPage = useCallback(() => {
    setOffset((prev) => prev + pageSize);
  }, [pageSize]);

  const hasNextPage = query.data?.workspaces.length === pageSize;

  return {
    ...query,
    data: allWorkspaces.length > 0 ? allWorkspaces : query.data?.workspaces,
    sandboxUrl: query.data?.sandboxUrl ?? null,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage: query.isFetching && offset > 0,
    newlyLoadedIds,
  };
}

/**
 * Hook for fetching all workspaces without type filtering.
 * Use this for unified workspace lists.
 */
export function useWorkspaces() {
  return useWorkspacesInternal({ pageSize: DEFAULT_PAGE_SIZE });
}

export function useInvalidateWorkspaces() {
  const utils = trpc.useUtils();

  return useCallback(() => {
    utils.chat.listWorkspaces.invalidate();
    utils.chat.getActiveChats.invalidate();
  }, [utils]);
}
